<template>
  <!-- 편집기 호스트: Vuex activeEditor 상태에 따라 알맞은 드로어/모달을 렌더링 -->
  <div class="editor-drawer-host">
    <!-- 히어로 배너 편집 드로어 -->
    <hero-editor />

    <!-- 통계 편집 드로어 -->
    <stats-editor />

    <!-- 파트너 편집 드로어 -->
    <partners-editor />

    <!-- CTA 편집 드로어 -->
    <c-t-a-editor />

    <!-- 기능 카드 편집 모달 -->
    <feature-card-editor
      :visible="isFeatureModalVisible"
      :card="featurePayload.card"
      :card-index="featurePayload.cardIndex"
      @save="handleFeatureSave"
      @cancel="handleEditorClose"
    />

    <!-- 보도자료 편집 모달 -->
    <press-card-editor
      :visible="isPressModalVisible"
      :article="pressPayload.article"
      :article-index="pressPayload.articleIndex"
      @save="handlePressSave"
      @cancel="handleEditorClose"
      @delete="handlePressDelete"
    />

    <!-- 가격안내 카드 편집 모달 -->
    <pricing-card-editor
      :visible="isPricingModalVisible"
      :plan="pricingPayload.plan"
      :plan-index="pricingPayload.planIndex"
      @cancel="handleEditorClose"
    />

    <!-- 서비스 섹션 편집 드로어 -->
    <service-section-editor
      :visible="isServiceDrawerVisible"
      :section="servicePayload.section"
      :slug="servicePayload.slug"
      :data="servicePayload.data"
      @cancel="handleEditorClose"
    />

    <!-- 다운로드 앱 편집 모달 -->
    <download-app-editor
      :visible="isDownloadModalVisible"
      :app="downloadPayload.app"
      :app-index="downloadPayload.appIndex"
      :category="downloadPayload.category"
      @cancel="handleEditorClose"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import HeroEditor from './editors/HeroEditor.vue';
import StatsEditor from './editors/StatsEditor.vue';
import PartnersEditor from './editors/PartnersEditor.vue';
import CTAEditor from './editors/CTAEditor.vue';
import FeatureCardEditor from './editors/FeatureCardEditor.vue';
import PressCardEditor from './editors/PressCardEditor.vue';
import PricingCardEditor from './editors/PricingCardEditor.vue';
import ServiceSectionEditor from './editors/ServiceSectionEditor.vue';
import DownloadAppEditor from './editors/DownloadAppEditor.vue';

export default {
  name: 'EditorDrawerHost',

  components: {
    HeroEditor,
    StatsEditor,
    PartnersEditor,
    CTAEditor,
    FeatureCardEditor,
    PressCardEditor,
    PricingCardEditor,
    ServiceSectionEditor,
    DownloadAppEditor,
  },

  computed: {
    ...mapState('homepageEditor', ['activeEditor']),
    ...mapGetters('homepageEditor', ['currentPageData']),

    // 기능 카드 모달 표시 여부
    isFeatureModalVisible() {
      return (
        this.activeEditor?.type === 'modal' &&
        this.activeEditor?.sectionKey === 'features'
      );
    },

    // 보도자료 모달 표시 여부
    isPressModalVisible() {
      return (
        this.activeEditor?.type === 'modal' &&
        this.activeEditor?.sectionKey === 'press'
      );
    },

    // 기능 카드 모달에 전달할 payload
    featurePayload() {
      if (!this.isFeatureModalVisible) {
        return { card: {}, cardIndex: -1 };
      }
      return {
        card: this.activeEditor?.payload?.card || {},
        cardIndex: this.activeEditor?.payload?.cardIndex ?? -1,
      };
    },

    // 보도자료 모달에 전달할 payload
    pressPayload() {
      if (!this.isPressModalVisible) {
        return { article: null, articleIndex: -1 };
      }
      return {
        article: this.activeEditor?.payload?.article || null,
        articleIndex: this.activeEditor?.payload?.articleIndex ?? -1,
      };
    },

    // 가격안내 카드 모달 표시 여부
    isPricingModalVisible() {
      return (
        this.activeEditor?.type === 'modal' &&
        this.activeEditor?.sectionKey === 'pricingCard'
      );
    },

    // 가격안내 카드 모달에 전달할 payload
    pricingPayload() {
      if (!this.isPricingModalVisible) {
        return { plan: null, planIndex: -1 };
      }
      return {
        plan: this.activeEditor?.payload?.plan || null,
        planIndex: this.activeEditor?.payload?.planIndex ?? -1,
      };
    },

    // 서비스 섹션 드로어 표시 여부
    isServiceDrawerVisible() {
      return (
        this.activeEditor?.type === 'drawer' &&
        this.activeEditor?.sectionKey === 'serviceSection'
      );
    },

    // 서비스 섹션 드로어에 전달할 payload
    servicePayload() {
      if (!this.isServiceDrawerVisible) {
        return { section: 'hero', slug: '', data: null };
      }
      return {
        section: this.activeEditor?.payload?.section || 'hero',
        slug: this.activeEditor?.payload?.slug || '',
        data: this.activeEditor?.payload?.data || null,
      };
    },

    // 다운로드 앱 모달 표시 여부
    isDownloadModalVisible() {
      return (
        this.activeEditor?.type === 'modal' &&
        this.activeEditor?.sectionKey === 'downloadApp'
      );
    },

    // 다운로드 앱 모달에 전달할 payload
    downloadPayload() {
      if (!this.isDownloadModalVisible) {
        return { app: null, appIndex: -1, category: 'hospital' };
      }
      return {
        app: this.activeEditor?.payload?.app || null,
        appIndex: this.activeEditor?.payload?.appIndex ?? -1,
        category: this.activeEditor?.payload?.category || 'hospital',
      };
    },
  },

  methods: {
    // 편집기 닫기 (activeEditor 초기화)
    handleEditorClose() {
      this.$store.commit('homepageEditor/SET_ACTIVE_EDITOR', null);
    },

    // 기능 카드 저장 처리: Vuex 데이터 업데이트 후 모달 닫기
    handleFeatureSave({ card, cardIndex }) {
      const featuresSection = this.currentPageData?.sections?.find(
        (s) => s.sectionKey === 'features'
      );
      if (!featuresSection) {
        this.handleEditorClose();
        return;
      }

      const cards = [...(featuresSection.data?.cards || [])];
      if (cardIndex >= 0 && cardIndex < cards.length) {
        // 기존 카드 업데이트
        cards[cardIndex] = card;
      } else {
        // 새 카드 추가
        cards.push(card);
      }

      this.$store.commit('homepageEditor/UPDATE_SECTION', {
        page: 'main',
        sectionKey: 'features',
        data: { ...featuresSection.data, cards },
      });

      this.handleEditorClose();
    },

    // 보도자료 저장 처리: Vuex press 배열 업데이트 후 모달 닫기
    handlePressSave({ article, articleIndex }) {
      if (!this.currentPageData) {
        this.handleEditorClose();
        return;
      }

      const press = [...(this.currentPageData.press || [])];
      if (articleIndex >= 0 && articleIndex < press.length) {
        // 기존 기사 업데이트
        press[articleIndex] = article;
      } else {
        // 신규 기사 추가
        press.push(article);
      }

      // press는 pageData.main.press에 직접 저장
      this.$store.commit('homepageEditor/SET_PAGE_DATA', {
        page: 'main',
        data: { ...this.currentPageData, press },
      });

      this.handleEditorClose();
    },

    // 보도자료 삭제 처리: Vuex press 배열에서 해당 항목 제거 후 모달 닫기
    handlePressDelete({ articleIndex }) {
      if (!this.currentPageData) {
        this.handleEditorClose();
        return;
      }

      const press = [...(this.currentPageData.press || [])];
      if (articleIndex >= 0 && articleIndex < press.length) {
        press.splice(articleIndex, 1);
      }

      this.$store.commit('homepageEditor/SET_PAGE_DATA', {
        page: 'main',
        data: { ...this.currentPageData, press },
      });

      this.handleEditorClose();
    },
  },
};
</script>

<style scoped>
.editor-drawer-host {
  /* 드로어/모달 렌더링 컨테이너 - 레이아웃에 영향 없음 */
  display: contents;
}
</style>
